// «Viendo ahora» — who has this conversation open (plugin `com.satoritech.viewing`, shared/API.md
// §39, 2026-10-17). Twin of the native `ViewingPresence` written from the same contract.
//
// Pure: decoding the plugin's answers and its `custom_com.satoritech.viewing_changed` event, the
// in-memory reduction keyed by (channel, root), and what the UI draws — viewers minus me, newest
// `since` first, up to three faces + «+N», «Viendo ahora: Ana, Luis», and whether a DM's partner
// is looking («Viendo el chat» outranks «En línea»). Never persisted.
import { tf } from '../i18n/i18n';

export const VIEWING_PLUGIN_ID = 'com.satoritech.viewing';
export const VIEWING_API = `/plugins/${VIEWING_PLUGIN_ID}/api/v1/viewing`;
export const VIEWING_EVENT = `custom_${VIEWING_PLUGIN_ID}_changed`;
/** Heartbeat cadence while a conversation stays on screen; the server forgets an entry 50 s after the last. */
export const VIEWING_HEARTBEAT_MS = 20_000;
export const VIEWING_MAX_FACES = 3;

export interface Viewer {
  userId: string;
  since: number;
}

export interface ViewingChange {
  channelId: string;
  rootId: string;
  viewers: Viewer[];
}

/** One scope: a channel's timeline (`rootId` "") or one thread. */
export function key(channelId: string, rootId: string): string {
  return `${channelId}|${rootId ?? ''}`;
}

/**
 * The `viewers` list of an answer or an event. `null` IS the empty set (the plugin RPC drops empty
 * slices — measured live); a JSON string is unwrapped (Mattermost re-encodes some nested values);
 * entries without a user id are dropped; one entry per user (the latest `since`).
 */
export function decodeViewers(raw: unknown): Viewer[] {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const byUser = new Map<string, Viewer>();
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const userId = typeof e.user_id === 'string' ? e.user_id : '';
    if (!userId) continue;
    const since = typeof e.since === 'number' && Number.isFinite(e.since) ? e.since : 0;
    const known = byUser.get(userId);
    if (!known || since > known.since) byUser.set(userId, { userId, since });
  }
  return [...byUser.values()];
}

/** `POST`/`GET` answer `{"viewers":[…]}`. */
export function decodeAnswer(raw: unknown): Viewer[] {
  if (!raw || typeof raw !== 'object') return [];
  return decodeViewers((raw as Record<string, unknown>).viewers);
}

/** The WS event's data; null when it names no channel. */
export function decodeEvent(data: Record<string, unknown> | undefined, broadcastChannelId?: string): ViewingChange | null {
  if (!data) return null;
  const channelId = (typeof data.channel_id === 'string' && data.channel_id) || broadcastChannelId || '';
  if (!channelId) return null;
  const rootId = typeof data.root_id === 'string' ? data.root_id : '';
  return { channelId, rootId, viewers: decodeViewers(data.viewers) };
}

/** What the UI shows: everyone but me (and anyone else excluded), newest `since` first, ties by id. */
export function others(viewers: readonly Viewer[], me: string | null, exclude: ReadonlySet<string> = new Set()): Viewer[] {
  return viewers
    .filter((v) => v.userId !== me && !exclude.has(v.userId))
    .sort((a, b) => b.since - a.since || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0));
}

/** The header stack: up to three faces (newest first = drawn on top) and the «+N» beyond them. */
export function stack(shown: readonly Viewer[], max = VIEWING_MAX_FACES): { faces: Viewer[]; overflow: number } {
  return { faces: shown.slice(0, max), overflow: Math.max(0, shown.length - max) };
}

/** «Viendo ahora: Ana, Luis» — every viewer named, in the stack's order. */
export function tooltip(names: readonly string[]): string {
  return tf('web.viewing.now', names.join(', '));
}

/** A DM: is the other person looking at it right now? */
export function partnerIsViewing(viewers: readonly Viewer[], partnerId: string | null | undefined, me: string | null): boolean {
  return !!partnerId && partnerId !== me && viewers.some((v) => v.userId === partnerId);
}

/** Same set (order-free, `since` included)? A refetch that changes nothing must not notify. */
export function sameSet(a: readonly Viewer[], b: readonly Viewer[]): boolean {
  if (a.length !== b.length) return false;
  const m = new Map(a.map((v) => [v.userId, v.since]));
  return b.every((v) => m.get(v.userId) === v.since);
}

/**
 * The in-memory table: one scope's set REPLACED by each answer/event (the plugin always sends the
 * whole set). An empty set removes the scope. Returns the same map when nothing changed.
 */
export function reduce(table: ReadonlyMap<string, Viewer[]>, change: ViewingChange): ReadonlyMap<string, Viewer[]> {
  const k = key(change.channelId, change.rootId);
  const before = table.get(k) ?? [];
  if (sameSet(before, change.viewers)) return table;
  const next = new Map(table);
  if (change.viewers.length) next.set(k, change.viewers);
  else next.delete(k);
  return next;
}

export const ViewingPresence = { key, decodeViewers, decodeAnswer, decodeEvent, others, stack, tooltip, partnerIsViewing, sameSet, reduce } as const;
